import { useInfiniteQuery } from '@tanstack/react-query';
import { Interval } from 'luxon';

import { getMenuItemInsightForEntity, MenuItemSortable } from 'apis/merchant-gateway/insights';
import { EntityType } from 'common/types';

import { MenuTableSortableColumnIDs } from '../components/tables/menu/types';
import { SortOrder, SortState } from '../components/tables/types';

type MenuInsightParams = {
  entityType: EntityType;
  entityId: string;
  interval: Interval;
  sortBy?: MenuTableSortableColumnIDs;
  sortOrder?: SortOrder;
  limit?: number;
};

const NUM_ITEMS_PER_PAGE = 20;
const sortableFields: MenuItemSortable[] = ['SALES_WITH_VAT', 'ORDERS', 'UNITS', 'NAME'];

export const DEFAULT_MENU_SORT_STATE: SortState<MenuTableSortableColumnIDs> = {
  sortBy: 'UNITS',
  sortOrder: SortOrder.DESC,
};

export const transformToAPISortableField = (value?: MenuTableSortableColumnIDs): MenuItemSortable | undefined => {
  switch (value) {
    case 'SALES_WITH_VAT_PERCENTAGE':
      return 'SALES_WITH_VAT';
    case 'UNITS_PERCENTAGE':
      return 'UNITS';
    default:
      return sortableFields.find(sortable => sortable === value);
  }
};

const useMenuInsights = ({ entityId, entityType, interval, sortBy, sortOrder, limit }: MenuInsightParams) => {
  const sortableField = transformToAPISortableField(sortBy);
  return useInfiniteQuery(
    ['menu-insights', entityId, entityType, interval, sortableField, sortOrder, limit],
    ({ pageParam: cursor }) =>
      getMenuItemInsightForEntity({
        entityType,
        entityId,
        interval,
        limit: limit ?? NUM_ITEMS_PER_PAGE,
        cursor,
        sortBy: sortableField,
        sortOrder,
      }),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
      getNextPageParam: lastPage => lastPage.pagination?.next,
      getPreviousPageParam: firstPage => firstPage.pagination?.previous,
      keepPreviousData: false,
    },
  );
};

export default useMenuInsights;
